import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeIn, SpotlightImage } from "@/components/Reveal";
import { BUYER_FORM_URL, VENDOR_FORM_URL } from "@/constants/links";

const LEADERS_IMG = "https://images.unsplash.com/photo-1758519290830-5462f4924bb5?q=85&w=1600&auto=format&fit=crop";
const GOLD_IMG = "https://images.unsplash.com/photo-1786835567286-ba8d77f61ecf?q=85&w=1600&auto=format&fit=crop";

const audiences = [
  { name: "Individual Buyers", copy: "People seeking convenient and secure access to products and services online." },
  { name: "Vendors & SMEs", copy: "Local sellers, small businesses, and service providers looking to expand their reach and increase sales." },
  { name: "Corporate & Business Clients", copy: "Organizations sourcing products, services, and logistics solutions at scale." },
  { name: "Dispatch & Logistics Partners", copy: "Independent riders and logistics providers earning through structured delivery operations." },
  { name: "Strategic Partners & Investors", copy: "Partners and investors seeking long-term value in a scalable digital marketplace." },
];

const values = [
  { n: "01", title: "Trust by design", copy: "Verification, transparency and buyer protection sit at the foundation — not in the fine print." },
  { n: "02", title: "Craft over clutter", copy: "We curate. Every department, every vendor, every detail earns its place." },
  { n: "03", title: "Access for all", copy: "Borders should describe maps, not shopping carts. We build for open access." },
  { n: "04", title: "Proudly Nigerian, truly global", copy: "Rooted in Lagos ambition, engineered for the world stage." },
];

const phases = [
  { n: "I", title: "Foundation", tag: "NOW", copy: "Brand, vision and this website. The buyer waitlist and vendor interest registration are open.", active: true },
  { n: "II", title: "The Build", tag: "NEXT", copy: "The marketplace platform — storefronts, payments and cross-border logistics — in development.", active: false },
  { n: "III", title: "Launch", tag: "AHEAD", copy: "Doors open. Waitlist members and registered vendors step in first.", active: false },
];

export default function About() {
  return (
    <>
      {/* Hero */}
      <section data-testid="about-hero" className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24 lg:pt-48">
        <div className="pointer-events-none absolute -top-24 right-[-8%] h-[28rem] w-[28rem] rounded-full bg-[#1E3A8A]/12 blur-[110px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <p className="eyebrow" data-testid="about-eyebrow">About Aflimart Technologies LTD</p>
          <h1 className="mt-8 font-sans text-4xl font-bold leading-[1.06] tracking-tight text-[#0F172A] sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>Technology, crafted</MaskedLine>
            <MaskedLine delay={0.3} className="text-gold-gradient">for a borderless</MaskedLine>
            <MaskedLine delay={0.45}>continent.</MaskedLine>
          </h1>
          <FadeIn delay={0.7}>
            <div className="mt-8 max-w-2xl space-y-5 text-base leading-relaxed text-[#64748B] sm:text-lg" data-testid="about-company-description">
              <p>AFLIMART DIGITAL MARKET LTD is a technology-driven digital marketplace designed to connect buyers, sellers, service providers, and logistics partners on one unified platform.</p>
              <p>Our platform enables individuals and businesses to buy and sell products and services conveniently while providing fast, secure, and reliable delivery solutions through our dispatch and logistics network.</p>
              <p>AFLIMART DIGITAL MARKET LTD is built to empower local vendors, expand market access, create employment opportunities, and simplify digital commerce across communities. By leveraging technology, innovation, and strategic partnerships, we aim to transform everyday trading into a seamless digital experience.</p>
              <p>We are committed to transparency, trust, customer satisfaction, and sustainable growth while supporting small businesses and entrepreneurs to scale globally from their local environment.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Vision & Mission */}
      <section data-testid="about-mission-vision" className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
        <FadeIn>
          <h2 className="font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl" data-testid="vision-mission-title">Our Vision &amp; Mission</h2>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <FadeIn className="lg:col-span-5">
            <div className="card-dark h-full p-8 sm:p-10" data-testid="vision-card">
              <span className="eyebrow">Our Vision</span>
              <p className="mt-6 font-sans text-xl leading-relaxed text-[#0F172A] sm:text-2xl">
                To become Africa’s most trusted and inclusive digital marketplace, empowering individuals and businesses to trade, grow, and thrive through innovative technology and seamless commerce solutions.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1} className="lg:col-span-7">
            <div className="card-dark h-full p-8 sm:p-10" data-testid="mission-card">
              <span className="eyebrow">Our Mission</span>
              <ul className="mt-6 space-y-5">
                {[
                  "To connect buyers, sellers, service providers, and logistics partners on one secure platform.",
                  "To support local vendors and businesses with digital tools that expand market access.",
                  "To provide fast, reliable, and transparent delivery solutions.",
                  "To create employment opportunities and promote economic inclusion.",
                ].map((m, i) => (
                  <li key={i} data-testid={`mission-point-${i + 1}`} className="flex items-start gap-4 text-base leading-relaxed text-[#475569] sm:text-lg">
                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#1E3A8A]" aria-hidden="true" />
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Who We Serve */}
      <section data-testid="about-who-we-serve" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-24 lg:px-16">
        <FadeIn>
          <h2 className="font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl" data-testid="who-we-serve-title">Who We Serve</h2>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {audiences.map((a, i) => (
            <FadeIn key={a.name} delay={0.06 * i}>
              <div data-testid={`audience-card-${i + 1}`} className="card-dark group h-full p-8 sm:p-10 hover:-translate-y-1">
                <span className="font-sans text-sm tracking-[0.25em] text-[#1E3A8A]/50">0{i + 1}</span>
                <h3 className="mt-5 font-sans text-xl text-[#0F172A] transition-colors duration-500 group-hover:text-[#1D4ED8] sm:text-2xl">{a.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#64748B] sm:text-base">{a.copy}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Founder */}
      <section data-testid="about-founder" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-24 lg:px-16">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <FadeIn className="lg:col-span-5">
              <div className="relative mx-auto max-w-sm lg:mx-0" data-testid="founder-portrait-frame">
                <div className="absolute -inset-6 rounded-full bg-[#1E3A8A]/10 blur-3xl animate-pulse-glow" aria-hidden="true" />
                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[#1E3A8A]/25 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
                  <img
                    src="/assets/founder-jose-leonard.jpg"
                    alt="Jose I Leonard, Founder & CEO of Aflimart Technologies LTD"
                    data-testid="founder-portrait"
                    className="h-full w-full object-cover"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#08080A]/50 via-transparent to-transparent" />
                  <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-gold/20" />
                </div>
              </div>
            </FadeIn>
            <div className="lg:col-span-7">
              <p className="eyebrow" data-testid="founder-eyebrow">Meet the Founder</p>
              <h2 className="mt-6 font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl" data-testid="founder-name">Jose I Leonard</h2>
              <p className="mt-3 font-sans text-xs tracking-[0.2em] text-[#1E3A8A]/80" data-testid="founder-role">Founder & CEO, Aflimart Technologies LTD</p>
              <FadeIn delay={0.15}>
                <div className="mt-8 space-y-5 text-base leading-relaxed text-[#64748B] sm:text-lg">
                  <p>Aflimart was founded with a simple but ambitious vision — to make commerce more trusted, accessible, structured, and convenient for individuals and businesses across Africa.</p>
                  <p>As Founder & CEO, Jose I Leonard is leading the development of Aflimart with a focus on technology, trust, innovation, vendor empowerment, and seamless commerce solutions.</p>
                  <p>Through Aflimart Digital Market, the goal is to connect buyers, vendors, service providers, and logistics partners on one unified platform while creating opportunities for businesses and communities to grow.</p>
                </div>
                <blockquote className="mt-10 border-l-2 border-[#1E3A8A] pl-6 font-sans text-xl italic text-[#1D4ED8] sm:text-2xl" data-testid="founder-quote">
                  ‘World Market in Your Palm.’
                </blockquote>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16">
          <p className="eyebrow">What We Stand For</p>
          <h2 className="mt-6 font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl">
            <MaskedLine view delay={0.1}>Values, set in gold.</MaskedLine>
          </h2>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {values.map((v, i) => (
              <FadeIn key={v.n} delay={0.07 * i}>
                <div data-testid={`value-card-${i + 1}`} className="card-dark group h-full p-8 sm:p-10 hover:-translate-y-1">
                  <span className="font-sans text-sm tracking-[0.25em] text-[#1E3A8A]/50">{v.n}</span>
                  <h3 className="mt-5 font-sans text-xl text-[#0F172A] transition-colors duration-500 group-hover:text-[#1D4ED8] sm:text-2xl">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#64748B] sm:text-base">{v.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Imagery strip */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
          <FadeIn>
            <SpotlightImage src={LEADERS_IMG} alt="African corporate leadership" testId="about-image-leaders" aspect="aspect-[16/10]" />
          </FadeIn>
          <FadeIn delay={0.12}>
            <SpotlightImage src={GOLD_IMG} alt="Curated luxury goods in gold tones" testId="about-image-gold" aspect="aspect-[16/10]" />
          </FadeIn>
        </div>
      </section>

      {/* Roadmap */}
      <section data-testid="about-roadmap" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16">
          <p className="eyebrow">Where We Are</p>
          <h2 className="mt-6 font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl">
            <MaskedLine view delay={0.1}>The road to launch.</MaskedLine>
          </h2>
          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            {phases.map((p, i) => (
              <FadeIn key={p.n} delay={0.08 * i}>
                <div
                  data-testid={`roadmap-phase-${i + 1}`}
                  className={`relative h-full rounded-2xl border p-8 sm:p-10 ${
                    p.active
                      ? "border-[#1E3A8A]/50 bg-gradient-to-b from-[#EFF6FF] to-[#F2F4F7] shadow-[0_0_60px_-20px_rgba(30,58,138,0.25)]"
                      : "border-[#E2E8F0] bg-gradient-to-b from-white to-[#F2F4F7]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-sans text-3xl text-[#1E3A8A]/40">{p.n}</span>
                    <span className={`rounded-full border px-3 py-1 font-sans text-[10px] tracking-[0.25em] ${p.active ? "border-[#1E3A8A]/60 text-[#1E3A8A]" : "border-[#E2E8F0] text-[#94A3B8]"}`}>
                      {p.tag}
                    </span>
                  </div>
                  <h3 className="mt-6 font-sans text-xl text-[#0F172A] sm:text-2xl">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#64748B]">{p.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <div className="mt-14 flex flex-wrap gap-4">
              <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="about-cta-buyer" className="btn-gold">
                Join the Waitlist <ArrowUpRight size={16} />
              </a>
              <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="about-cta-vendor" className="btn-ghost">
                Become a Vendor
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
